import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api.js';

// "Before you go" — the shift standards checklist (CJ 2026-08-09, D249).
//
// CJ: "they often don't or forget or leave without doing it. it creates
// hostility and frustration and then the managers do not have the ability to do
// much since everyone keeps quiet on it. this allows for an issue to be
// reported with proof."
//
// So this is not a checklist, it is a signature. Each employee confirms, in
// their own name and with a timestamp, the state they left the store in. When
// the next shift reports the dishes undone, the manager has a specific claim to
// talk about instead of a grievance nobody will own.
//
// Three deliberate absences:
//   * No "tick all". A button that clears the list is a button that gets
//     pressed on the way out of the door — the exact behaviour this exists to
//     stop. One tap per item, every time.
//   * No list of who HASN'T ticked. "2 of 3 on shift confirmed" is useful
//     pressure; naming the holdout to their colleagues is a nastier product.
//   * Nothing here blocks clocking out. This is a record, not a gate — a gate
//     would just teach people to tick without looking.
export function BeforeYouGo({ currentUser }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try {
      setData(await api('GET', '/api/shift-standards', null, headers));
    } catch (e) { setErr(e?.message || String(e)); }
  }, [currentUser.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  async function toggle(it) {
    setBusyId(it.task_id); setErr('');
    const q = `location_id=${data.location_id}&date=${data.date}&period_id=${data.period.id}`;
    try {
      if (it.mine) {
        // Un-ticking has to be possible: the honest response to a misclick
        // must not be to leave a false claim standing.
        await api('DELETE', `/api/shift-standards/${it.task_id}/complete?${q}`, null, headers);
      } else {
        await api('POST', `/api/shift-standards/${it.task_id}/complete`, {
          location_id: data.location_id, date: data.date, period_id: data.period.id,
        }, headers);
      }
      await load();
    } catch (e) {
      let m = e?.message || 'Could not save that.';
      try { m = JSON.parse(m).error || m; } catch (_) {}
      setErr(m);
    } finally { setBusyId(null); }
  }

  if (!data || !data.period || data.total === 0) return null;

  const allDone = data.done === data.total;
  const soon = data.period.minutes_left != null && data.period.minutes_left <= 90;

  return (
    <div className="card" style={{ borderLeft: `4px solid ${allDone ? 'var(--forest)' : soon ? '#ef6c00' : '#b48a47'}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 6 }}>
        <div>
          <div className="text-base font-semibold">Before you go</div>
          <div style={{ fontSize: 12, color: '#757575', marginTop: 2 }}>
            Each shift leaves the next one with a clean and stocked store.
          </div>
        </div>
        <div style={{ fontSize: 12, fontWeight: 700, color: allDone ? '#2e5d42' : '#8a5a1f' }}>
          {data.done} of {data.total}
        </div>
      </div>

      {err && (
        <div style={{ fontSize: 12, color: '#a8323a', background: '#faeceb', borderRadius: 6, padding: '6px 10px', marginTop: 8 }}>
          {err}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
        {data.items.map((it) => (
          <button key={it.task_id} onClick={() => toggle(it)} disabled={busyId === it.task_id}
            style={{ display: 'flex', alignItems: 'flex-start', gap: 10, textAlign: 'left',
                     border: `1px solid ${it.mine ? '#bcd6c4' : '#e0e0e0'}`, borderRadius: 8,
                     padding: '10px 12px', cursor: 'pointer', fontFamily: 'inherit',
                     background: it.mine ? '#f1f7f2' : '#fff', width: '100%' }}>
            <span style={{ width: 20, height: 20, borderRadius: 5, flexShrink: 0, marginTop: 1,
                           border: `2px solid ${it.mine ? 'var(--forest)' : '#c4c9ce'}`,
                           background: it.mine ? 'var(--forest)' : '#fff', color: '#fff',
                           display: 'flex', alignItems: 'center', justifyContent: 'center',
                           fontSize: 13, fontWeight: 700, lineHeight: 1 }}>
              {it.mine ? '✓' : ''}
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 13.5, fontWeight: 700,
                             color: it.mine ? '#2e5d42' : '#212121' }}>{it.title}</span>
              {it.description && (
                <span style={{ display: 'block', fontSize: 12, color: '#546e7a', marginTop: 1 }}>{it.description}</span>
              )}
              {it.others > 0 && (
                <span style={{ display: 'block', fontSize: 11.5, color: '#757575', marginTop: 2 }}>
                  {it.others} other{it.others === 1 ? '' : 's'} on this shift confirmed it
                </span>
              )}
            </span>
          </button>
        ))}
      </div>

      <div style={{ fontSize: 11.5, color: '#757575', marginTop: 10 }}>
        {allDone
          ? 'Thanks — your name and the time are on each of these.'
          : 'Tap each one as you finish it. Your name and the time are recorded against it.'}
      </div>
    </div>
  );
}
